import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { fetchV3TabData } from '../services/api';
import { useGlobalSSE } from '../contexts/GlobalSSEContext';
import { getTimezone, saveTimezone, TIMEZONE_EMPTY } from '../utils/timezone';
import { buildFullWorldState, mergeProfile } from './utils/v3StateHelpers';
import { handleV3Update, handleV3Init, handleV3Patch } from './utils/v3EventHandlers';
import { perfMark } from '../utils/perfTracker';
import { feLog } from '../utils/frontendLogger';

// Get initial timezone from cookie (set during auth)
// Returns null if not loaded, TIMEZONE_EMPTY if checked but not set, or IANA string if set
const getInitialTimezone = () => {
    const tz = getTimezone();
    // Convert TIMEZONE_EMPTY marker to null for display purposes
    // (null means "not set" in the UI context)
    if (tz === TIMEZONE_EMPTY) return null;
    return tz;
};

// Custom hook for V3 state management using global SSE
export function useV3State() {
    const token = useContext(AuthContext);
    const { registerHandler, unregisterHandler, connectionState, startConnection } = useGlobalSSE();
    const [lastConnectionState, setLastConnectionState] = useState(connectionState);

    const [state, setState] = useState({
        profile: {
            username: 'Loading...',
            level: 0,
            exp: 0,
            maxExp: 1,
            health: 0,
            maxHealth: 1,
            mana: 0,
            maxMana: 1,
            steps: 0,
            maxSteps: 10,
            stamina: 0,
            staminaCap: 1,
            staminaRegenTime: 0,
            isGM: false,
            timezone: getInitialTimezone(), // Initialize from cookie (set during auth)
        },
        map: null, // Flat array of cells
        mapSize: null, // Comes from server
        regionCard: null,
        position: { x: 0, y: 0 },
        loading: true,
        error: null,
    });

    // Function to update timezone in state (called from Settings)
    // Also saves to cookie for persistence across sessions
    // Memoized to prevent infinite loops in useEffect dependencies
    const setTimezone = useCallback((timezone) => {
        // Save to cookie for persistence
        saveTimezone(timezone);
        // Update state
        setState(prev => ({
            ...prev,
            profile: {
                ...prev.profile,
                timezone
            }
        }));
    }, []);

    useEffect(() => {
        if (!token) return;

        let mounted = true;
        let initialLoadComplete = false;

        // SSE event handler — registered immediately to catch all events,
        // but v3Init is skipped during initial load to prevent its heavy
        // JSON.parse + React re-render from blocking the main thread
        // while the HTTP fetch response continuation is waiting.
        const handleWorldEvent = (eventType, data) => {
            if (!mounted) return;

            // Skip v3Init during initial HTTP load — the HTTP fetch provides
            // the same data without blocking the main thread.
            if (eventType === 'v3Init' && !initialLoadComplete) {
                return;
            }

            switch (eventType) {
                case 'v3Update':
                    handleV3Update(data, setState);
                    break;

                case 'v3Init':
                    handleV3Init(data, setState);
                    break;

                case 'v3Patch':
                    handleV3Patch(data, setState);
                    break;

                default:
                    break;
            }
        };

        // Register handler immediately so v3Patch events are not lost
        registerHandler('world_v3', handleWorldEvent);

        // Load initial data via HTTP (faster than waiting for SSE v3Init)
        const loadInitialData = async () => {
            try {
                perfMark('fetchStart');
                const data = await fetchV3TabData(token, 'world_v3');
                perfMark('fetchResponse');

                if (!mounted) return;

                // Handle structured data directly (no widgets)
                if (data && data.data) {
                    perfMark('buildStart');
                    setState(prev => buildFullWorldState(data.data, prev));
                    perfMark('dataLoaded');
                } else if (data && data.widgets && data.widgets.length > 0) {
                    // Fallback to widget format for compatibility
                    const worldWidget = data.widgets.find(w => w.type === 'world-data');
                    if (worldWidget && worldWidget.data) {
                        setState(prev => buildFullWorldState(worldWidget.data, prev));
                    } else {
                        throw new Error('No world data in response');
                    }
                } else {
                    throw new Error('No data received');
                }

                // Allow v3Init events from now on (for SSE reconnections)
                initialLoadComplete = true;

                // Now that initial data is loaded, start SSE connection.
                // This prevents SSE v3Init from blocking the main thread
                // during the HTTP fetch.
                startConnection();
            } catch (error) {
                console.error('[useV3State] Failed to load initial data:', error);
                feLog('error', 'v3state_init_failed', String(error), {
                    err_name: error.name || '',
                    err_msg: error.message || '',
                });
                if (mounted) {
                    setState(prev => ({
                        ...prev,
                        loading: false,
                        error: error.message || 'Failed to load initial data'
                    }));
                }
                // Enable v3Init as fallback so SSE can provide data
                initialLoadComplete = true;
                startConnection();
            }
        };

        loadInitialData();

        // Cleanup
        return () => {
            mounted = false;
            unregisterHandler('world_v3');
        };
    }, [token, registerHandler, unregisterHandler, startConnection]);

    // Monitor connection state changes
    useEffect(() => {
        // If connection was restored (was false, now true)
        if (!lastConnectionState && connectionState && token) {
            console.log('[useV3State] Connection restored, refreshing data');

            // Request fresh data after reconnection
            const refreshData = async () => {
                try {
                    const result = await fetchV3TabData(token, 'world_v3');
                    if (result.data) {
                        // Process the initial data, preserving client-only fields like timezone
                        setState(prevState => ({
                            ...prevState,
                            profile: result.data.profile
                                ? mergeProfile(prevState.profile, result.data.profile)
                                : prevState.profile,
                            map: result.data.map || prevState.map,
                            regionCard: result.data.regionCard || prevState.regionCard,
                            myLands: result.data.myLands || prevState.myLands,
                            scanState: result.data.scanState || prevState.scanState,
                            loading: false
                        }));
                    }
                } catch (err) {
                    console.error('[useV3State] Failed to refresh after reconnect:', err);
                    feLog('error', 'v3state_reconnect_refresh_failed', String(err), {
                        err_msg: err.message || '',
                    });
                }
            };

            // Small delay to ensure SSE is fully reconnected
            setTimeout(refreshData, 500);
        }

        setLastConnectionState(connectionState);
    }, [connectionState, lastConnectionState, token]);

    // Expose function to immediately update LandWatch data
    // This is used for instant UI feedback from action responses
    useEffect(() => {
        window.v3UpdateLandWatch = (landWatchData) => {
            setState(prev => ({
                ...prev,
                regionCard: {
                    ...prev.regionCard,
                    landWatch: landWatchData
                }
            }));
        };

        return () => {
            delete window.v3UpdateLandWatch;
        };
    }, []);

    // Extract player stats for the UI
    const stats = useMemo(() => {
        if (!state.profile) return { health: 0, maxHealth: 1, mana: 0, maxMana: 1, steps: 0, maxSteps: 10 };

        return {
            health: state.profile.health || 0,
            maxHealth: state.profile.maxHealth || 1,
            mana: state.profile.mana || 0,
            maxMana: state.profile.maxMana || 1,
            steps: state.profile.steps || state.profile.stamina || 0,
            maxSteps: state.profile.maxSteps || state.profile.staminaCap || 10,
        };
    }, [state.profile]);

    return {
        ...state,
        stats,
        setTimezone, // Function to update timezone from Settings
    };
}
